import { render } from "preact";
import { useRef } from "preact/hooks";
import { type Signal, signal } from "@preact/signals";
import { canLook } from "../atmosphere.ts";
import { clearRecords, loadRecords } from "../records.ts";
import { readSave, restoreSave, type SaveData, saveFile } from "../savedata.ts";
import { clearPieces, loadPieces, piecesModel } from "../shelf.ts";
import { sounds } from "../sound.ts";
import { download } from "./download.ts";
import styles from "./settings.module.css";

/** How finely the scene is drawn: in the screen's own pixels, or one to a CSS pixel. */
export type Quality = "fine" | "light";
/** The clay's colour while it is kneaded, each with its own glow where the brush works. */
export type ClayColor = "brown" | "gray";

const SAVED = "kneading-needed.settings";

function loadSaved(): {
  quality?: Quality;
  clay?: ClayColor;
  still?: boolean;
  atmosphere?: boolean;
  sound?: number;
  music?: number;
} {
  try {
    return JSON.parse(localStorage.getItem(SAVED) ?? "{}");
  } catch {
    // No storage (or nothing sensible in it): the defaults.
    return {};
  }
}

const saved = loadSaved();

/** A volume kept (0 to 1), or `usual` if there is none that makes sense. */
const volumeOf = (kept: unknown, usual: number) =>
  typeof kept === "number" && kept >= 0 && kept <= 1 ? kept : usual;

/** The player's settings, kept in this browser; the game follows them (see main.ts). */
export const settings = {
  quality: signal<Quality>(saved.quality === "light" ? "light" : "fine"),
  /** Only while kneading: a finished piece keeps its finish's colour. */
  clay: signal<ClayColor>(saved.clay === "gray" ? "gray" : "brown"),
  /** No drift of the workshop's camera, no turning of pieces on show. */
  still: signal(saved.still ?? matchMedia("(prefers-reduced-motion: reduce)").matches),
  /**
   * A request's air, and how a piece kneaded freely comes across, are looked at by the
   * vision-language model (see atmosphere.ts and tags.ts), which is fetched on first use.
   * Unless the player has chosen, it is wherever the model can run, save on touch devices such
   * as phones: there, even with WebGPU, looking can stall or freeze the browser.
   */
  atmosphere: signal(saved.atmosphere ?? false),
  /** How loud the sounds are (0 to 1; see sound.ts), and the workshop's music. */
  sound: signal(volumeOf(saved.sound, 0.8)),
  music: signal(volumeOf(saved.music, 0.5)),
};

if (saved.atmosphere === undefined && !matchMedia("(pointer: coarse)").matches) {
  void canLook().then((able) => {
    if (able) settings.atmosphere.value = true;
  });
}

/** Keeps the settings as they are now. */
export function saveSettings(): void {
  try {
    localStorage.setItem(
      SAVED,
      JSON.stringify({
        quality: settings.quality.value,
        clay: settings.clay.value,
        still: settings.still.value,
        atmosphere: settings.atmosphere.value,
        sound: settings.sound.value,
        music: settings.music.value,
      }),
    );
  } catch {
    // No storage: the settings last until the page is closed.
  }
}

/** The settings screen, opened by the game. */
export interface Settings {
  /** Shows the settings; `onClose` runs when the player closes them. */
  open(onClose: () => void): void;
  /** The way back, as 閉じる goes (null while they are not shown). */
  readonly wayBack: (() => void) | null;
}

/** What can be cleared away, and how much of it there is (null: it could not be read). */
type Kept = "shelf" | "ledger";
const KEPT: Record<Kept, { name: string; unit: string; action: string; ask: string }> = {
  shelf: {
    name: "棚の作品",
    unit: "点",
    action: "すべて下ろす",
    ask: "棚の作品をすべて下ろします。納品票の画像やこね手順も消え、元に戻せません。",
  },
  ledger: {
    name: "依頼帳の記録",
    unit: "件",
    action: "白紙にする",
    ask:
      "依頼帳の記録・お題と腕試しの達成をすべて消し、依頼の解放を最初に戻します。元に戻せません。棚の作品はそのままです。",
  },
};

/** The settings screen's state, beyond the settings themselves. */
interface State {
  open: Signal<boolean>;
  counts: Record<Kept, Signal<number | null>>;
  /** What is being asked about once more: clearing away, or reading a save file in. */
  asking: Signal<Kept | "import" | null>;
  /** The save file chosen to read in, waiting on the answer. */
  pending: Signal<{ data: SaveData; name: string } | null>;
  /** What just came of writing out or reading in, and whether it went well. */
  notice: Signal<{ text: string; ok: boolean } | null>;
  busy: Signal<boolean>;
}

interface Actions {
  clear(kept: Kept): void;
  writeSave(): void;
  writeModel(): void;
  choose(file: File): void;
  readIn(): void;
  close(): void;
}

const today = () => {
  const d = new Date(), pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
};

/**
 * How the game is drawn and moves, and what has been kept: written out to a file (and the
 * shelf as a 3D model), read back in, or cleared away.
 */
export function mountSettings(root: HTMLElement): Settings {
  const state: State = {
    open: signal(false),
    counts: { shelf: signal(null), ledger: signal(null) },
    asking: signal(null),
    pending: signal(null),
    notice: signal(null),
    busy: signal(false),
  };
  let onClose = () => {};
  const count = async () => {
    state.counts.shelf.value = await loadPieces().then((p) => p.length, () => null);
    state.counts.ledger.value = await loadRecords().then((r) => r.size, () => null);
  };
  /** Runs `work` with the buttons held, noting how it went. */
  const run = async (work: () => Promise<string>, failed: string) => {
    state.busy.value = true;
    try {
      const text = await work();
      state.notice.value = text ? { text, ok: true } : null;
    } catch {
      state.notice.value = { text: failed, ok: false };
    }
    state.busy.value = false;
  };
  const actions: Actions = {
    async clear(kept) {
      state.asking.value = null;
      await run(async () => {
        await (kept === "shelf" ? clearPieces() : clearRecords());
        await count();
        return kept === "shelf" ? "棚の作品を下ろしました" : "依頼帳と進み具合を白紙にしました";
      }, "消せませんでした。記録は変更していません");
    },
    writeSave: () =>
      run(async () => {
        download(await saveFile(), `kneading-needed-${today()}.json`);
        return "セーブデータを書き出しました";
      }, "書き出せませんでした"),
    writeModel: () =>
      run(async () => {
        // The whole shelf, oldest first.
        const pieces = (await loadPieces()).sort((a, b) => a.date - b.date);
        if (pieces.length === 0) return "";
        download(await piecesModel(pieces), `kneading-needed-shelf-${today()}.glb`);
        return "3D モデルを書き出しました";
      }, "書き出せませんでした"),
    choose: (file) =>
      run(async () => {
        state.pending.value = { data: await readSave(file), name: file.name };
        state.asking.value = "import";
        return "";
      }, "ねこ商会のセーブデータとして読めませんでした"),
    readIn: () =>
      run(async () => {
        const { data } = state.pending.value!;
        state.asking.value = null;
        state.pending.value = null;
        await restoreSave(data);
        await count();
        return "セーブデータを読み込みました";
      }, "読み込めませんでした"),
    close() {
      state.open.value = false;
      state.asking.value = null;
      state.pending.value = null;
      state.notice.value = null;
      onClose();
    },
  };
  render(<SettingsScreen state={state} actions={actions} />, root);
  return {
    open(back) {
      onClose = back;
      state.open.value = true;
      void count();
    },
    get wayBack() {
      return state.open.value ? actions.close : null;
    },
  };
}

function SettingsScreen({ state, actions }: { state: State; actions: Actions }) {
  const fileInput = useRef<HTMLInputElement>(null);
  if (!state.open.value) return null;
  const set = <T,>(s: Signal<T>, value: T) => {
    s.value = value;
    saveSettings();
  };
  const { counts, asking, pending, busy } = state;
  const ask = (kept: Kept) => {
    asking.value = kept;
    pending.value = null;
  };
  return (
    <div class={styles.settings}>
      <div class={styles.card}>
        <h2 class={styles.heading}>設定</h2>

        <p class={styles.groupTitle}>表示</p>
        <div class={styles.setting}>
          <div class={styles.label}>
            <b>画質</b>
            <small>
              かるいは画面を粗めに描いて、動きを軽くします（記念撮影の写真も粗くなります）
            </small>
          </div>
          <div class={styles.choice} role="group" aria-label="画質">
            {([["fine", "きれい"], ["light", "かるい"]] as const).map(([quality, name]) => (
              <button
                key={quality}
                type="button"
                aria-pressed={settings.quality.value === quality}
                onClick={() => set(settings.quality, quality)}
              >
                {name}
              </button>
            ))}
          </div>
        </div>
        <div class={styles.setting}>
          <div class={styles.label}>
            <b>粘土の色</b>
            <small>
              こねている間の粘土と、ブラシが効く所の光の色です（茶色は水色、灰色はオレンジに光ります）。焼き上がりや仕上がりの色は変わりません
            </small>
          </div>
          <div class={styles.choice} role="group" aria-label="粘土の色">
            {([["brown", "茶色"], ["gray", "灰色"]] as const).map(([clay, name]) => (
              <button
                key={clay}
                type="button"
                aria-pressed={settings.clay.value === clay}
                onClick={() => set(settings.clay, clay)}
              >
                {name}
              </button>
            ))}
          </div>
        </div>
        <label class={styles.setting}>
          <div class={styles.label}>
            <b>動きを減らす</b>
            <small>工房のカメラのゆれと、納品や棚で作品がくるくる回るのを止めます</small>
          </div>
          <input
            class={styles.switch}
            type="checkbox"
            checked={settings.still.value}
            onChange={(e) => set(settings.still, e.currentTarget.checked)}
          />
        </label>

        <p class={styles.groupTitle}>音</p>
        {([
          [settings.sound, "効果音", "こねる音・窯・ハンコ・こねこの声など"],
          [settings.music, "BGM", "工房に流れる静かな音楽"],
        ] as const).map(([volume, name, line]) => (
          <div key={name} class={styles.setting}>
            <div class={styles.label}>
              <b>{name}</b>
              <small>{line}</small>
            </div>
            <input
              class={styles.volume}
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={volume.value}
              aria-label={`${name}の音量`}
              onInput={(e) => volume.value = Number(e.currentTarget.value)}
              onChange={(e) => {
                set(volume, Number(e.currentTarget.value));
                // How loud the clay now squishes.
                if (volume === settings.sound) sounds.press("push");
              }}
            />
          </div>
        ))}

        <p class={styles.groupTitle}>雰囲気</p>
        <label class={styles.setting}>
          <div class={styles.label}>
            <b>雰囲気を見てもらう</b>
            <small>
              依頼の雰囲気（やさしい丸みなど）と自由作品の見立て（かわいい・動物っぽいなど）を、画像を読む
              AI にこの端末の中で見てもらいます。初めてのときに約 850MB を読み込みます（WebGPU
              が必要で、使える端末では初めからオンです。スマホなどのタッチの端末では重すぎることがあるので、初めはオフです）。点数には入りません
            </small>
          </div>
          <input
            class={styles.switch}
            type="checkbox"
            checked={settings.atmosphere.value}
            onChange={(e) => set(settings.atmosphere, e.currentTarget.checked)}
          />
        </label>

        <p class={styles.groupTitle}>データ</p>
        <div class={styles.data}>
          <div class={styles.setting}>
            <div class={styles.label}>
              <b>セーブデータ</b>
              <small>棚の作品と依頼帳の記録を、ファイルにしてほかのブラウザへ持っていけます</small>
            </div>
            <button
              type="button"
              class={styles.action}
              disabled={busy.value}
              onClick={actions.writeSave}
            >
              書き出す
            </button>
            <button
              type="button"
              class={styles.action}
              disabled={busy.value}
              onClick={() => fileInput.current?.click()}
            >
              読み込む
            </button>
            <input
              ref={fileInput}
              type="file"
              accept=".json,application/json"
              hidden
              onChange={(e) => {
                const file = e.currentTarget.files?.[0];
                e.currentTarget.value = "";
                if (file) actions.choose(file);
              }}
            />
          </div>
          <div class={styles.setting}>
            <div class={styles.label}>
              <b>棚の作品の 3D モデル</b>
              <small>すべての作品を台座ごと並べて、GLB ファイルに書き出します</small>
            </div>
            <button
              type="button"
              class={styles.action}
              disabled={busy.value || !counts.shelf.value}
              onClick={actions.writeModel}
            >
              書き出す
            </button>
          </div>
          {asking.value === "import" && pending.value && (
            <div class={styles.ask}>
              <p>
                「{pending.value.name}」から、棚の作品 {pending.value.data.shelf.length}{" "}
                点と依頼帳の記録 {Object.keys(pending.value.data.records).length}{" "}
                件を読み込みます。今の棚の作品と依頼帳の記録は消えて、読み込んだものに置き換わります。
              </p>
              <div class="row">
                <button type="button" class="reset" onClick={() => asking.value = null}>
                  やめる
                </button>
                <button type="button" class={styles.danger} onClick={actions.readIn}>
                  読み込む
                </button>
              </div>
            </div>
          )}
          {state.notice.value && (
            <p class={styles.notice} role="status" data-ok={state.notice.value.ok}>
              {state.notice.value.text}
            </p>
          )}
        </div>
        {(Object.keys(KEPT) as Kept[]).map((kept) => {
          const { name, unit, action, ask: question } = KEPT[kept], n = counts[kept].value;
          return (
            <div key={kept} class={styles.data}>
              <div class={styles.setting}>
                <div class={styles.label}>
                  <b>{name}</b>
                  <small>{n === null ? "読み込めません" : `${n} ${unit}`}</small>
                </div>
                <button
                  type="button"
                  class={styles.clear}
                  disabled={!n || busy.value || asking.value === kept}
                  onClick={() => ask(kept)}
                >
                  {action}
                </button>
              </div>
              {asking.value === kept && (
                <div class={styles.ask}>
                  <p>{question}</p>
                  <div class="row">
                    <button type="button" class="reset" onClick={() => asking.value = null}>
                      やめる
                    </button>
                    <button
                      type="button"
                      class={styles.danger}
                      onClick={() => actions.clear(kept)}
                    >
                      {action}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        <div class={`row ${styles.actions}`}>
          <button type="button" class="reset" onClick={actions.close}>閉じる</button>
        </div>
      </div>
    </div>
  );
}
